import { ExcelDateToJSDate } from "../../utils";

/**
 * Company-specific field mappings
 * This can be extended for different companies/clients
 */
export const COMPANY_FIELD_MAPPINGS = {
  default: {
    loadNumber: {
      aliases: ["LOAD #", "Load Number", "Load #", "Job #", "Job Number"],
      type: "load",
      transform: (value) => String(value).trim(),
    },
    orderNumber: {
      aliases: ["ORDER #", "Order Number", "Order #", "Container #"],
      type: "order",
      transform: (value) => String(value).toUpperCase().trim(),
    },
    date: {
      aliases: ["DATE", "Load Date", "Pickup Date", "Transaction Date"],
      type: "load",
      transform: (value) => {
        if (!value) return null;

        // Check if it's a number (Excel date)
        if (typeof value === "number") {
          return ExcelDateToJSDate(value);
        }

        // Try regular date parsing
        const date = new Date(value);
        return isNaN(date) ? value : date;
      },
    },
    baseRate: {
      aliases: ["RATE", "Base Rate", "Rate"],
      type: "load",
      transform: (value) => {
        const numValue = parseFloat(String(value).replace(/[$,]/g, ""));
        return isNaN(numValue) ? 0 : numValue;
      },
    },
    fuelRate: {
      aliases: ["FSC", "Fuel Surcharge", "Fuel Rate", "FUEL RATE"],
      type: "load",
      transform: (value) => {
        const numValue = parseFloat(String(value).replace(/[$,]/g, ""));
        return isNaN(numValue) ? 0 : numValue;
      },
    },
    client: {
      aliases: ["CLIENT", "Customer", "Client Name", "Customer Name"],
      type: "load",
      transform: (value) => String(value).trim(),
    },
    city: {
      aliases: ["CITY", "City Name", "Destination City"],
      type: "order",
      transform: (value) => String(value).trim(),
    },
    customerName: {
      aliases: ["CUST NAME", "Customer", "Client"],
      type: "order",
      transform: (value) => String(value).trim(),
    },
  },
  company1: {
    // Inherit from default and override specific fields
    loadNumber: {
      aliases: ["Job #", "Job Number", "Load #"],
      type: "load",
      transform: (value) => String(value).trim(),
    },
    // Add company-specific fields here
  },
  company2: {
    // Different mappings for another company
    loadNumber: {
      aliases: ["Work Order #", "WO #", "Load Number"],
      type: "load",
      transform: (value) => String(value).trim(),
    },
    // Add more company-specific fields
  },
};

/**
 * Default sheet configurations
 */
export const DEFAULT_SHEET_CONFIGS = {
  LOAD: {
    isCollection: false,
    label: "Load Information",
    columns: {
      loadNumber: {
        label: "Load Number",
        possibleHeaders: ["LOAD #", "Load Number", "Load ID", "Job #"],
        transform: (value) => String(value).trim(),
        searchHorizontal: true,
        adjacentCells: 1,
        required: true,
      },
      date: {
        label: "Date",
        possibleHeaders: ["DATE", "Load Date", "Transaction Date"],
        transform: (value) => {
          if (!value) return null;

          // Check if it's a number (Excel date)
          if (typeof value === "number") {
            return ExcelDateToJSDate(value);
          }

          // Try regular date parsing
          const date = new Date(value);
          return isNaN(date) ? value : date;
        },
        searchHorizontal: true,
        adjacentCells: 1,
      },
      baseRate: {
        label: "Base Rate",
        possibleHeaders: ["RATE", "Base Rate"],
        transform: (value) => {
          const numValue = parseFloat(String(value).replace(/[$,]/g, ""));
          return isNaN(numValue) ? 0 : numValue;
        },
      },
      fuelRate: {
        label: "Fuel Rate",
        possibleHeaders: ["FUEL RATE", "Fuel Rate", "FSC"],
        transform: (value) => {
          const numValue = parseFloat(String(value).replace(/[$,]/g, ""));
          return isNaN(numValue) ? 0 : numValue;
        },
      },
      client: {
        label: "Client",
        possibleHeaders: ["CLIENT", "Customer", "Client Name"],
        transform: (value) => String(value).trim(),
      },
    },
  },
  ORDERS: {
    isCollection: true,
    label: "Orders",
    columns: {
      orderNumber: {
        label: "Order Number",
        possibleHeaders: ["ORDER #", "Order Number", "Order .", "Order"],
        transform: (value) => String(value).trim(),
        required: true,
      },
      city: {
        label: "City",
        possibleHeaders: ["CITY", "City Name"],
        transform: (value) => String(value).trim(),
      },
      customerName: {
        label: "Customer Name",
        possibleHeaders: ["CUST NAME", "Customer", "Client"],
        transform: (value) => String(value).trim(),
      },
    },
  },
};

/**
 * Generate sheet configurations based on company
 */
export const generateSheetConfigs = (companyId = "default") => {
  const companyMappings =
    COMPANY_FIELD_MAPPINGS[companyId] || COMPANY_FIELD_MAPPINGS.default;

  return {
    LOAD: {
      columns: Object.entries(companyMappings)
        .filter(([_, config]) => config.type === "load")
        .reduce((acc, [field, config]) => {
          acc[field] = {
            label: field.charAt(0).toUpperCase() + field.slice(1),
            possibleHeaders: config.aliases,
            transform: config.transform,
            type: typeof config.defaultValue,
            adjacentIndex: config.adjacentIndex || 1,
          };
          return acc;
        }, {}),
    },
    ORDERS: {
      headerIdentifier: companyMappings.orderNumber?.aliases[0] || "ORDER #",
      columns: Object.entries(companyMappings)
        .filter(([_, config]) => config.type === "order")
        .reduce((acc, [field, config]) => {
          acc[field] = {
            label: field.charAt(0).toUpperCase() + field.slice(1),
            possibleHeaders: config.aliases,
            transform: config.transform,
            type: typeof config.defaultValue,
          };
          return acc;
        }, {}),
    },
  };
};

/**
 * Create extraction configuration for specific use cases
 */
export const createExtractionConfig = (options = {}) => {
  const {
    companyId = "default",
    configType = "LOAD",
    customFields = {},
    validationRules = {},
  } = options;

  const baseConfig = generateSheetConfigs(companyId);
  const selectedConfig = baseConfig[configType] || baseConfig.LOAD;

  // Merge with custom fields
  const mergedColumns = {
    ...selectedConfig.columns,
    ...customFields,
  };

  return {
    ...selectedConfig,
    columns: mergedColumns,
    validation: {
      requiredFields: Object.entries(mergedColumns)
        .filter(([_, config]) => config.required)
        .map(([field]) => field),
      uniqueFields: validationRules.uniqueFields || [],
      ...validationRules,
    },
  };
};

/**
 * Helper function to find column config by header name
 */
export const findColumnConfig = (config, header) => {
  if (!config?.columns) return null;

  return Object.entries(config.columns).find(([_, columnConfig]) =>
    columnConfig.possibleHeaders?.some(
      (h) => h.toLowerCase() === header.toLowerCase()
    )
  )?.[1];
};

/**
 * Helper function to get standardized field name from header
 */
export const getFieldNameFromHeader = (config, header) => {
  if (!config?.columns) return header;

  const entry = Object.entries(config.columns).find(([_, columnConfig]) =>
    columnConfig.possibleHeaders?.some(
      (h) => h.toLowerCase() === header.toLowerCase()
    )
  );

  return entry ? entry[0] : header;
};

/**
 * Validate parsed data against configuration
 */
export const validateParsedData = (data, config) => {
  const errors = [];
  const warnings = [];

  if (!Array.isArray(data) || data.length === 0) {
    errors.push("No data to validate");
    return { isValid: false, errors, warnings };
  }

  const { validation } = config;

  if (validation?.requiredFields) {
    data.forEach((row, index) => {
      validation.requiredFields.forEach((field) => {
        if (!row[field] || row[field] === "") {
          errors.push(`Row ${index + 1}: Missing required field '${field}'`);
        }
      });
    });
  }

  if (validation?.uniqueFields) {
    validation.uniqueFields.forEach((field) => {
      const values = data.map((row) => row[field]).filter(Boolean);
      const duplicates = values.filter(
        (value, index) => values.indexOf(value) !== index
      );

      if (duplicates.length > 0) {
        errors.push(
          `Duplicate values found in field '${field}': ${[
            ...new Set(duplicates),
          ].join(", ")}`
        );
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Transform data based on configuration
 */
export const transformData = (data, config) => {
  if (!Array.isArray(data) || !config?.columns) return data;

  return data.map((row) => {
    const transformedRow = {};

    Object.entries(config.columns).forEach(([field, columnConfig]) => {
      if (row[field] !== undefined) {
        let value = row[field];

        // Apply transformation if provided
        if (
          columnConfig.transform &&
          typeof columnConfig.transform === "function"
        ) {
          try {
            value = columnConfig.transform(value);
          } catch (error) {
            console.warn(`Transform error for ${field}:`, error);
          }
        }

        transformedRow[field] = value;
      }
    });

    return transformedRow;
  });
};

/**
 * Export commonly used configurations
 */
export const PRESET_CONFIGS = {
  TRUCKING_LOAD: createExtractionConfig({
    configType: "LOAD",
    validationRules: {
      requiredFields: ["loadNumber", "date"],
      uniqueFields: ["loadNumber"],
    },
  }),

  TRUCKING_ORDERS: createExtractionConfig({
    configType: "ORDERS",
    validationRules: {
      requiredFields: ["orderNumber"],
      uniqueFields: ["orderNumber"],
    },
  }),

  SERVICE_JOBS: createExtractionConfig({
    customFields: {
      serviceType: {
        label: "Service Type",
        possibleHeaders: ["Service Type", "Job Type", "Work Type"],
        transform: (value) => String(value).trim(),
        required: true,
      },
      equipmentType: {
        label: "Equipment Type",
        possibleHeaders: ["Equipment", "Equipment Type", "Unit Type"],
        transform: (value) => String(value).trim(),
      },
    },
    validationRules: {
      requiredFields: ["loadNumber", "serviceType"],
    },
  }),

  // PDF-specific configurations
  PDF_TRUCKING_LOAD: {
    fileType: "pdf",
    extractionMethod: "text",
    patterns: {
      loadNumber: {
        label: "Load Number",
        patterns: [
          /(?:load\s*#?|load\s*number|job\s*#?|job\s*number)[\s:]*([A-Z0-9\-]+)/i,
          /^([A-Z0-9\-]{4,})\s*$/m,
        ],
        transform: (value) => String(value).trim().toUpperCase(),
        required: true,
      },
      date: {
        label: "Date",
        patterns: [
          /(?:date|pickup\s*date|delivery\s*date)[\s:]*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/i,
          /(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/g,
        ],
        transform: (value) => {
          if (!value) return null;
          const date = new Date(value);
          return isNaN(date) ? value : date.toISOString().split("T")[0];
        },
      },
      baseRate: {
        label: "Base Rate",
        patterns: [
          /(?:rate|base\s*rate|amount)[\s:]*\$?([0-9,]+\.?\d*)/i,
          /\$([0-9,]+\.?\d*)/g,
        ],
        transform: (value) => {
          const numValue = parseFloat(String(value).replace(/[$,]/g, ""));
          return isNaN(numValue) ? 0 : numValue;
        },
      },
      client: {
        label: "Client",
        patterns: [
          /(?:client|customer|company)[\s:]*([A-Za-z\s&.,'-]+)/i,
          /^([A-Z][A-Za-z\s&.,'-]+(?:Inc|LLC|Corp|Ltd)\.?)$/m,
        ],
        transform: (value) => String(value).trim(),
      },
    },
    validation: {
      requiredFields: ["loadNumber"],
      uniqueFields: ["loadNumber"],
    },
  },

  PDF_SERVICE_JOB: {
    fileType: "pdf",
    extractionMethod: "text",
    patterns: {
      jobNumber: {
        label: "Job Number",
        patterns: [
          /(?:job\s*#?|work\s*order|service\s*#?)[\s:]*([A-Z0-9\-]+)/i,
          /^(WO[A-Z0-9\-]+|JOB[A-Z0-9\-]+)$/m,
        ],
        transform: (value) => String(value).trim().toUpperCase(),
        required: true,
      },
      serviceType: {
        label: "Service Type",
        patterns: [
          /(?:service\s*type|job\s*type|work\s*type)[\s:]*([A-Za-z\s]+)/i,
          /(plumbing|hvac|electrical|repair|maintenance|installation)/i,
        ],
        transform: (value) => String(value).trim(),
        required: true,
      },
      customerName: {
        label: "Customer Name",
        patterns: [
          /(?:customer|client|name)[\s:]*([A-Za-z\s.,'-]+)/i,
          /^([A-Z][a-z]+\s+[A-Z][a-z]+)$/m,
        ],
        transform: (value) => String(value).trim(),
      },
      address: {
        label: "Address",
        patterns: [
          /(?:address|location)[\s:]*([0-9]+\s+[A-Za-z\s.,'-]+)/i,
          /(\d+\s+[A-Za-z\s.,'-]+(?:St|Ave|Rd|Dr|Ln|Blvd|Way)\.?)/i,
        ],
        transform: (value) => String(value).trim(),
      },
      amount: {
        label: "Amount",
        patterns: [
          /(?:total|amount|cost|price)[\s:]*\$?([0-9,]+\.?\d*)/i,
          /\$([0-9,]+\.?\d*)/g,
        ],
        transform: (value) => {
          const numValue = parseFloat(String(value).replace(/[$,]/g, ""));
          return isNaN(numValue) ? 0 : numValue;
        },
      },
    },
    validation: {
      requiredFields: ["jobNumber", "serviceType"],
      uniqueFields: ["jobNumber"],
    },
  },
};

/**
 * Extract data from PDF text using pattern matching
 */
export const extractPDFData = (text, config) => {
  if (!text || !config || !config.patterns) {
    return {};
  }

  const extractedData = {};

  Object.entries(config.patterns).forEach(([field, fieldConfig]) => {
    const { patterns, transform } = fieldConfig;
    let value = null;

    // Try each pattern until we find a match
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match) {
        // Use the first capturing group if available, otherwise the full match
        value = match[1] || match[0];
        break;
      }
    }

    if (value) {
      // Apply transformation if provided
      if (transform && typeof transform === "function") {
        try {
          value = transform(value);
        } catch (error) {
          console.warn(`Transform error for ${field}:`, error);
        }
      }

      extractedData[field] = value;
    }
  });

  return extractedData;
};

/**
 * Validate PDF extracted data
 */
export const validatePDFData = (data, config) => {
  const errors = [];
  const warnings = [];

  if (!data || Object.keys(data).length === 0) {
    errors.push("No data extracted from PDF");
    return { isValid: false, errors, warnings };
  }

  const { validation } = config;

  if (validation?.requiredFields) {
    validation.requiredFields.forEach((field) => {
      if (!data[field] || data[field] === "") {
        errors.push(`Missing required field '${field}'`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Create PDF extraction configuration
 */
export const createPDFExtractionConfig = (options = {}) => {
  const {
    patterns = {},
    validationRules = {},
    extractionMethod = "text",
  } = options;

  return {
    fileType: "pdf",
    extractionMethod,
    patterns,
    validation: {
      requiredFields: validationRules.requiredFields || [],
      uniqueFields: validationRules.uniqueFields || [],
      ...validationRules,
    },
  };
};

/**
 * Multi-sheet configuration for intelligent data selection
 */
export const MULTI_SHEET_CONFIGS = {
  TRUCKING_LOAD: {
    // Define sheet priorities and selection criteria
    sheetPriority: [
      {
        name: "LOAD PROCESS",
        priority: 1,
        description: "Load summary information",
        requiredFields: ["DROP #", "ORDER #"],
        preferredFields: ["SHIPMENT #", "CITY", "STATE", "ZIP", "FHC"],
        dataType: "single", // This sheet contains summary data
        selectionStrategy: "best_match", // Find best matching record
      },
      {
        name: "ORDERS",
        priority: 2,
        description: "Order details",
        requiredFields: ["Order .", "Order", "Cust Name"],
        preferredFields: ["City", "State", "Part Desc", "Order Qty"],
        dataType: "collection", // This sheet contains multiple records
        selectionStrategy: "first_valid", // Use first valid record
      },
      {
        name: "LOAD",
        priority: 3,
        description: "Load details",
        requiredFields: ["loadNumber"],
        preferredFields: ["transactionDate", "baseRate"],
        dataType: "single",
        selectionStrategy: "first_valid",
      },
    ],

    // Define how to merge data from multiple sheets
    mergeStrategy: {
      primary: "LOAD PROCESS", // Primary sheet for main load data
      supplements: ["ORDERS"], // Supplementary sheets for additional data
      conflictResolution: "primary_wins", // How to handle field conflicts
    },

    // Leg creation configuration - Comprehensive data capture
    legCreation: {
      enabled: true,
      sourceSheet: "ORDERS", // Sheet containing leg data
      groupingStrategy: "drop_number", // Group by drop number
      groupingField: "drop#", // Field name for grouping (from ORDERS sheet)
      sortBy: "drop#", // How to sort legs

      // Comprehensive field mapping - capture ALL ORDERS sheet data
      legFields: {
        // Core identification fields
        core: {
          dropNumber: "drop#",
          orderNumber: "Order .", // Primary field name, but code checks both "Order ." and "Order"
          orderNumberAlternatives: ["Order .", "Order", "orderNumber"], // Alternative field names to check
          lineNumber: "Line .",
        },

        // Customer and business information
        customer: {
          name: "Cust Name",
          purchaseOrder: "Cust PO",
        },

        // Complete delivery address (for backend location lookup)
        deliveryAddress: {
          line1: "Cust Addr Line 1",
          line2: "Cust Addr Line 2",
          line3: "Cust Addr Line 3",
          line4: "Cust Addr Line 4",
          city: "City",
          state: "State",
          zip: "Zip",
          // Combined address for backend lookup
          fullAddress: [
            "Cust Addr Line 1",
            "Cust Addr Line 2",
            "Cust Addr Line 3",
            "Cust Addr Line 4",
            "City",
            "State",
            "Zip",
          ],
        },

        // Product/item information (per line item)
        product: {
          partNumber: "Part.",
          description: "Part Desc",
          orderQuantity: "Order Qty",
          orderWeight: "Order Weight",
          backOrderQuantity: "Back Ord Qty",
          backOrderWeight: "Back Ord Weight",
        },

        // Shipping and logistics
        shipping: {
          shipTicket: "Ship Ticket",
          freightHandlingCode: "Frt Hdlg CD",
          loadPercent: "Load Percent",
          packsOrPallets: "Packs/Pallets",
        },

        // Status and workflow
        status: {
          holdCode: "Hold Code",
          nextStatus: "Next Status",
        },

        // Important dates
        dates: {
          orderDate: "OR Date",
          requestDate: "Request Date",
        },
      },

      // Data aggregation rules for leg-level summaries
      aggregation: {
        // Fields to sum across all items in a leg
        sumFields: [
          "Order Qty",
          "Order Weight",
          "Back Ord Qty",
          "Back Ord Weight",
        ],
        // Fields to concatenate (unique values only)
        concatenateFields: ["Ship Ticket", "Cust PO"],
        // Fields to take from primary order (first order in leg)
        primaryFields: [
          "Cust Name",
          "Cust Addr Line 1",
          "Cust Addr Line 2",
          "Cust Addr Line 3",
          "Cust Addr Line 4",
          "City",
          "State",
          "Zip",
          "Frt Hdlg CD",
        ],
      },
    },

    // Field mapping from sheet data to form fields
    fieldMapping: {
      // Load-related fields (from LOAD PROCESS sheet only - directional search)
      "LOAD #": {
        target: "loadNumber",
        searchDirection: "right", // Look for value to the right of "LOAD #" label
        maxDistance: 5, // Increased search distance
        sheetRestriction: "LOAD PROCESS", // Only search in LOAD PROCESS sheet
      },
      LOAD: {
        target: "loadNumber",
        searchDirection: "right", // Alternative search for just "LOAD" text
        maxDistance: 5,
        sheetRestriction: "LOAD PROCESS", // Only search in LOAD PROCESS sheet
      },
      DATE: {
        target: "transactionDate",
        searchDirection: "right", // Look for date value to the right of "DATE" label
        maxDistance: 3,
        sheetRestriction: "LOAD PROCESS", // Only search in LOAD PROCESS sheet
      },
      RATE: {
        target: "baseRate",
        searchDirection: "below", // Look for rate value below "RATE" label
        maxDistance: 3,
        sheetRestriction: "LOAD PROCESS", // Only search in LOAD PROCESS sheet
      },
      FSC: {
        target: "fuelRate",
        searchDirection: "below", // Look for FSC value below "FSC" label
        maxDistance: 3,
        sheetRestriction: "LOAD PROCESS", // Only search in LOAD PROCESS sheet
      },

      // LOAD PROCESS sheet fields (traditional table format)
      "DROP #": "dropNumber", // Map DROP # to dropNumber (this is the drop sequence)
      "SHIPMENT #": "shipmentNumber", // Map SHIPMENT # to shipmentNumber
      CITY: "deliveryCity", // Map CITY to deliveryCity
      STATE: "deliveryState", // Map STATE to deliveryState
      ZIP: "deliveryZip", // Map ZIP to deliveryZip
      FHC: "fhc", // Map FHC to fhc

      // Order-related fields (from ORDERS sheet only - traditional table format)
      "drop#": "dropNumber", // Map drop# to dropNumber (lowercase version, will be overridden by LOAD PROCESS if both exist)
      "Order .": "orderNumber",
      Order: "orderNumber", // Map Order (without period) to orderNumber
      Order: "orderNumber", // Map Order . to orderNumber (this is different from loadNumber)
      "Line .": "lineNumber", // Map Line . to lineNumber
      "Hold Code": "holdCode", // Map Hold Code to holdCode
      "Ship Ticket": "shipTicket", // Map Ship Ticket to shipTicket
      "Next Status": "nextStatus", // Map Next Status to nextStatus
      "OR Date": "orderDate", // Map OR Date to orderDate
      "Request Date": "requestDate", // Map Request Date to requestDate
      "Cust Name": "customerName", // Map Cust Name to customerName
      City: "deliveryCity", // Map City to deliveryCity
      State: "deliveryState", // Map State to deliveryState
      Zip: "deliveryZip", // Map Zip to deliveryZip
      "Part.": "partNumber", // Map Part. to partNumber
      "Part Desc": "partDescription", // Map Part Desc to partDescription
      "Order Qty": "orderQuantity", // Map Order Qty to orderQuantity
      "Order Weight": "orderWeight", // Map Order Weight to orderWeight
      "Back Ord Qty": "backOrderQuantity", // Map Back Ord Qty to backOrderQuantity
      "Back Ord Weight": "backOrderWeight", // Map Back Ord Weight to backOrderWeight
      "Load Percent": "loadPercent", // Map Load Percent to loadPercent
      "Packs/Pallets": "packsOrPallets", // Map Packs/Pallets to packsOrPallets
      "Frt Hdlg CD": "freightHandlingCode", // Map Frt Hdlg CD to freightHandlingCode
      "Cust PO": "customerPO", // Map Cust PO to customerPO
      "Cust Addr Line 1": "deliveryAddress1", // Map Cust Addr Line 1 to deliveryAddress1
      "Cust Addr Line 2": "deliveryAddress2", // Map Cust Addr Line 2 to deliveryAddress2
      "Cust Addr Line 3": "deliveryAddress3", // Map Cust Addr Line 3 to deliveryAddress3
      "Cust Addr Line 4": "deliveryAddress4", // Map Cust Addr Line 4 to deliveryAddress4

      // Alternative field names that might appear (keeping for backward compatibility)
      "Transaction Date": "transactionDate",
      "Base Rate": "baseRate",
      "Fuel Surcharge": "fuelRate",
      Notes: "note",
      Note: "note",
      Description: "note",
    },

    // Data validation and quality scoring
    validation: {
      requiredFields: ["loadNumber"], // Only require the essential field
      qualityScoring: {
        // Higher scores for more complete data
        completeness: 0.4, // 40% weight for field completeness
        accuracy: 0.3, // 30% weight for data accuracy
        relevance: 0.3, // 30% weight for field relevance
      },
    },
  },

  SERVICE_JOBS: {
    sheetPriority: [
      {
        name: "JOBS",
        priority: 1,
        description: "Service job details",
        requiredFields: ["Job Number", "Customer", "Service Type"],
        preferredFields: ["Address", "Phone", "Amount"],
        dataType: "collection",
        selectionStrategy: "first_valid",
      },
      {
        name: "SUMMARY",
        priority: 2,
        description: "Job summary",
        requiredFields: ["jobNumber"],
        preferredFields: ["totalAmount", "date"],
        dataType: "single",
        selectionStrategy: "best_match",
      },
    ],

    mergeStrategy: {
      primary: "JOBS",
      supplements: ["SUMMARY"],
      conflictResolution: "primary_wins",
    },

    fieldMapping: {
      "Job Number": "jobNumber",
      Customer: "customerName",
      "Service Type": "serviceType",
      Address: "serviceAddress",
      Phone: "customerPhone",
      Amount: "estimatedAmount",
      Date: "scheduledDate",
    },

    validation: {
      requiredFields: ["jobNumber", "customerName", "serviceType"],
      qualityScoring: {
        completeness: 0.5,
        accuracy: 0.3,
        relevance: 0.2,
      },
    },
  },
};

/**
 * Intelligent data selection from multi-sheet extracted data
 * Now merges data from all relevant sheets instead of selecting one primary
 */
export const selectBestDataForForm = (
  extractedData,
  configType = "TRUCKING_LOAD"
) => {
  const config = MULTI_SHEET_CONFIGS[configType];
  if (!config || !extractedData || extractedData.length === 0) {
    return {
      selectedData: {},
      legs: [],
      metadata: { source: "none", confidence: 0 },
    };
  }

  // Group data by sheet
  const dataBySheet = extractedData.reduce((acc, record) => {
    const sheetName = record._sheetName || "unknown";
    if (!acc[sheetName]) acc[sheetName] = [];
    acc[sheetName].push(record);
    return acc;
  }, {});

  // Initialize merged data object
  let mergedData = {};
  const sourcesUsed = [];

  // Process each sheet according to its configuration
  config.sheetPriority.forEach((sheetConfig) => {
    const sheetData = dataBySheet[sheetConfig.name] || [];

    if (sheetData.length === 0) {
      return;
    }

    // Debug: Show actual field names available in this sheet
    const sampleRecord = sheetData[0];
    const availableFields = Object.keys(sampleRecord).filter(
      (k) => !k.startsWith("_")
    );

    // For ORDERS sheet, we need ALL records for leg creation, not just the best one
    let recordsToProcess = [];
    if (sheetConfig.name === "ORDERS") {
      recordsToProcess = sheetData; // Use ALL records for ORDERS
    } else {
      // For other sheets, select the best record for form population
      recordsToProcess = [selectBestRecord(sheetData, sheetConfig, config)];
    }

    // Get the field mappings that are relevant to this sheet
    const relevantMappings = getRelevantFieldMappings(
      config.fieldMapping,
      availableFields,
      sheetConfig.name
    );

    // Process each record (for ORDERS this will be all records, for others just the best one)
    recordsToProcess.forEach((selectedRecord, index) => {
      // Apply field mapping for this sheet's data
      const mappedSheetData = applyFieldMapping(
        selectedRecord,
        relevantMappings,
        selectedRecord._rawSheetData
      );

      // For form population, only merge data from the first/best record of each sheet
      if (index === 0) {
        // Merge this sheet's data into the overall result for form population
        Object.entries(mappedSheetData).forEach(([key, value]) => {
          if (key.startsWith("_")) return; // Skip metadata

          // Only add if we don't already have this field or if the new value is better
          if (
            !mergedData[key] ||
            mergedData[key] === "" ||
            mergedData[key] === null
          ) {
            mergedData[key] = value;
          } else {
          }
        });
      }
    });

    sourcesUsed.push({
      sheet: sheetConfig.name,
      recordCount: sheetData.length,
      recordsProcessed: recordsToProcess.length,
      fieldsContributed: Object.keys(
        applyFieldMapping(
          recordsToProcess[0],
          relevantMappings,
          recordsToProcess[0]._rawSheetData
        )
      ).filter((k) => !k.startsWith("_")).length,
    });
  });

  // Create legs if leg creation is enabled
  let legs = [];
  if (config.legCreation && config.legCreation.enabled) {
    legs = createLegsFromData(dataBySheet, config);
  }

  return {
    selectedData: mergedData,
    legs: legs,
    metadata: {
      source: "merged",
      sourcesUsed: sourcesUsed,
      totalRecords: extractedData.length,
      sheetsProcessed: Object.keys(dataBySheet),
      legsCreated: legs.length,
      confidence: sourcesUsed.length > 0 ? 0.9 : 0,
    },
  };
};

/**
 * Get field mappings that are relevant to the available fields in a sheet
 * Now respects sheet restrictions to prevent conflicts
 */
const getRelevantFieldMappings = (
  allFieldMappings,
  availableFields,
  sheetName
) => {
  const relevantMappings = {};

  Object.entries(allFieldMappings).forEach(([sourceField, targetField]) => {
    // Handle directional search mappings (object-style mappings)
    if (
      typeof targetField === "object" &&
      targetField.target &&
      targetField.searchDirection
    ) {
      // Check if this mapping is restricted to a specific sheet
      if (targetField.sheetRestriction) {
        // Only include if we're processing the correct sheet
        if (sheetName === targetField.sheetRestriction) {
          relevantMappings[sourceField] = targetField;
        } else {
        }
      } else {
        // No restriction, include for all sheets
        relevantMappings[sourceField] = targetField;
      }
    }
    // Handle simple string mappings - check if this source field exists in the available fields
    else if (availableFields.includes(sourceField)) {
      relevantMappings[sourceField] = targetField;
    }
  });

  return relevantMappings;
};

/**
 * Calculate quality score for a sheet's data
 */
const calculateSheetScore = (sheetData, sheetConfig, globalConfig) => {
  if (!sheetData || sheetData.length === 0) {
    return 0;
  }

  const weights = globalConfig.validation.qualityScoring;
  let totalScore = 0;

  // Completeness score (how many required/preferred fields are present)
  const completenessScore = calculateCompletenessScore(sheetData, sheetConfig);
  totalScore += completenessScore * weights.completeness;

  // Accuracy score (data quality indicators)
  const accuracyScore = calculateAccuracyScore(sheetData, sheetConfig);
  totalScore += accuracyScore * weights.accuracy;

  // Relevance score (how well the data matches expected patterns)
  const relevanceScore = calculateRelevanceScore(sheetData, sheetConfig);
  totalScore += relevanceScore * weights.relevance;

  const finalScore = Math.min(totalScore, 1.0); // Cap at 1.0

  return finalScore;
};

/**
 * Calculate completeness score based on required and preferred fields
 */
const calculateCompletenessScore = (sheetData, sheetConfig) => {
  if (!sheetData.length) return 0;

  const sampleRecord = sheetData[0];
  const availableFields = Object.keys(sampleRecord).filter(
    (key) =>
      !key.startsWith("_") && // Exclude metadata fields
      sampleRecord[key] !== null &&
      sampleRecord[key] !== undefined &&
      sampleRecord[key] !== ""
  );

  const requiredFieldsPresent = sheetConfig.requiredFields.filter((field) =>
    availableFields.some(
      (available) =>
        available.toLowerCase().includes(field.toLowerCase()) ||
        field.toLowerCase().includes(available.toLowerCase())
    )
  ).length;

  const preferredFieldsPresent = sheetConfig.preferredFields.filter((field) =>
    availableFields.some(
      (available) =>
        available.toLowerCase().includes(field.toLowerCase()) ||
        field.toLowerCase().includes(available.toLowerCase())
    )
  ).length;

  const requiredScore =
    requiredFieldsPresent / sheetConfig.requiredFields.length;
  const preferredScore =
    preferredFieldsPresent / sheetConfig.preferredFields.length;

  return requiredScore * 0.7 + preferredScore * 0.3;
};

/**
 * Calculate accuracy score based on data quality indicators
 */
const calculateAccuracyScore = (sheetData, sheetConfig) => {
  if (!sheetData.length) return 0;

  let qualityIndicators = 0;
  let totalChecks = 0;

  sheetData.slice(0, 3).forEach((record) => {
    // Check first 3 records
    Object.entries(record).forEach(([key, value]) => {
      if (key.startsWith("_")) return; // Skip metadata

      totalChecks++;

      // Check for reasonable data patterns
      if (value && value !== "") {
        qualityIndicators++;

        // Additional quality checks
        if (key.toLowerCase().includes("date") && isValidDate(value)) {
          qualityIndicators += 0.5;
        }
        if (key.toLowerCase().includes("number") && !isNaN(value)) {
          qualityIndicators += 0.5;
        }
        if (
          key.toLowerCase().includes("name") &&
          typeof value === "string" &&
          value.length > 2
        ) {
          qualityIndicators += 0.5;
        }
      }
    });
  });

  return totalChecks > 0 ? Math.min(qualityIndicators / totalChecks, 1.0) : 0;
};

/**
 * Calculate relevance score based on expected data patterns
 */
const calculateRelevanceScore = (sheetData, sheetConfig) => {
  if (!sheetData.length) return 0;

  // Higher score for sheets that match their expected data type
  if (sheetConfig.dataType === "collection" && sheetData.length > 1) {
    return 0.8;
  }
  if (sheetConfig.dataType === "single" && sheetData.length === 1) {
    return 0.9;
  }
  if (sheetConfig.dataType === "single" && sheetData.length <= 3) {
    return 0.7;
  }

  return 0.5; // Default relevance
};

/**
 * Select the best record from a sheet based on strategy
 */
const selectBestRecord = (sheetData, sheetConfig, globalConfig) => {
  if (!sheetData || sheetData.length === 0) return {};

  switch (sheetConfig.selectionStrategy) {
    case "first_valid":
      return (
        sheetData.find((record) =>
          sheetConfig.requiredFields.some((field) =>
            Object.keys(record).some(
              (key) =>
                key.toLowerCase().includes(field.toLowerCase()) &&
                record[key] &&
                record[key] !== ""
            )
          )
        ) || sheetData[0]
      );

    case "best_match":
      // Score each record and return the best one
      return sheetData.reduce((best, current) => {
        const currentScore = calculateRecordScore(current, sheetConfig);
        const bestScore = calculateRecordScore(best, sheetConfig);
        return currentScore > bestScore ? current : best;
      }, sheetData[0]);

    default:
      return sheetData[0];
  }
};

/**
 * Calculate score for individual record
 */
const calculateRecordScore = (record, sheetConfig) => {
  if (!record) return 0;

  const nonEmptyFields = Object.entries(record).filter(
    ([key, value]) => !key.startsWith("_") && value && value !== ""
  ).length;

  const requiredFieldsPresent = sheetConfig.requiredFields.filter((field) =>
    Object.keys(record).some(
      (key) =>
        key.toLowerCase().includes(field.toLowerCase()) &&
        record[key] &&
        record[key] !== ""
    )
  ).length;

  return (
    (requiredFieldsPresent / sheetConfig.requiredFields.length) * 0.7 +
    (nonEmptyFields / Object.keys(record).length) * 0.3
  );
};

/**
 * Merge records from different sheets
 */
const mergeRecords = (primary, supplement, config) => {
  const merged = { ...primary };

  Object.entries(supplement).forEach(([key, value]) => {
    if (key.startsWith("_")) return; // Skip metadata

    // Apply conflict resolution strategy
    if (config.mergeStrategy.conflictResolution === "primary_wins") {
      if (!merged[key] || merged[key] === "") {
        merged[key] = value;
      }
    } else if (config.mergeStrategy.conflictResolution === "supplement_wins") {
      if (value && value !== "") {
        merged[key] = value;
      }
    }
  });

  return merged;
};

/**
 * Apply field mapping to convert sheet field names to form field names
 * Now supports directional searching for values relative to headers
 */
const applyFieldMapping = (data, fieldMapping, rawSheetData = null) => {
  const mapped = {};

  // First, process all directional search mappings explicitly
  if (rawSheetData) {
    Object.entries(fieldMapping).forEach(([key, mappingConfig]) => {
      if (
        typeof mappingConfig === "object" &&
        mappingConfig.target &&
        mappingConfig.searchDirection
      ) {
        const searchResult = performDirectionalSearch(
          key,
          mappingConfig,
          rawSheetData
        );

        if (searchResult !== null) {
          mapped[mappingConfig.target] = searchResult;
        } else {
        }
      }
    });
  }

  // Then process regular field mappings from the data
  Object.entries(data).forEach(([key, value]) => {
    if (key.startsWith("_")) return; // Skip metadata

    // Check if there's a mapping configuration for this key
    const mappingConfig = fieldMapping[key];

    if (mappingConfig) {
      // Handle object-style mapping with directional search
      if (typeof mappingConfig === "object" && mappingConfig.target) {
        // Skip if we already processed this via directional search above
        if (mapped[mappingConfig.target] !== undefined) {
          return;
        }

        // Fallback to current value if directional search didn't work
        mapped[mappingConfig.target] = value;
      } else if (typeof mappingConfig === "string") {
        // Handle simple string mapping
        mapped[mappingConfig] = value;
      }
    } else {
      // Check for partial matches (case-insensitive)
      const partialMatch = Object.entries(fieldMapping).find(
        ([sourceKey, config]) => {
          const targetKey = typeof config === "object" ? sourceKey : sourceKey;
          return (
            sourceKey.toLowerCase() === key.toLowerCase() ||
            key.toLowerCase().includes(sourceKey.toLowerCase()) ||
            sourceKey.toLowerCase().includes(key.toLowerCase())
          );
        }
      );

      if (partialMatch) {
        const [matchedKey, matchedConfig] = partialMatch;
        const targetField =
          typeof matchedConfig === "object"
            ? matchedConfig.target
            : matchedConfig;

        mapped[targetField] = value;
      } else {
        // Keep original key if no mapping found
        mapped[key] = value;
      }
    }
  });

  return mapped;
};

/**
 * Perform directional search to find values relative to header positions
 */
const performDirectionalSearch = (headerKey, mappingConfig, rawSheetData) => {
  const { searchDirection, maxDistance, searchPattern } = mappingConfig;

  // Find the header position in the raw sheet data
  let headerPosition = null;

  for (let rowIndex = 0; rowIndex < rawSheetData.length; rowIndex++) {
    const row = rawSheetData[rowIndex];
    for (let colIndex = 0; colIndex < row.length; colIndex++) {
      const cellValue = row[colIndex];
      if (
        cellValue &&
        cellValue.toString().toLowerCase().includes(headerKey.toLowerCase())
      ) {
        headerPosition = { row: rowIndex, col: colIndex };
        break;
      }
    }
    if (headerPosition) break;
  }

  if (!headerPosition) {
    return null;
  }

  // Search in the specified direction
  let result = null;
  switch (searchDirection) {
    case "right":
      result = searchRight(
        headerPosition,
        maxDistance,
        rawSheetData,
        searchPattern
      );
      break;
    case "below":
      result = searchBelow(
        headerPosition,
        maxDistance,
        rawSheetData,
        searchPattern
      );
      break;
    case "left":
      result = searchLeft(
        headerPosition,
        maxDistance,
        rawSheetData,
        searchPattern
      );
      break;
    case "above":
      result = searchAbove(
        headerPosition,
        maxDistance,
        rawSheetData,
        searchPattern
      );
      break;
    default:
      return null;
  }

  return result;
};

/**
 * Search for value to the right of header
 */
const searchRight = (headerPos, maxDistance, sheetData, searchPattern) => {
  const row = sheetData[headerPos.row];

  for (let i = 1; i <= maxDistance; i++) {
    const colIndex = headerPos.col + i;
    if (colIndex >= row.length) break;

    const cellValue = row[colIndex];
    if (cellValue && cellValue !== "") {
      if (!searchPattern || searchPattern.test(cellValue.toString())) {
        return cellValue;
      }
    }
  }

  return null;
};

/**
 * Search for value below header
 */
const searchBelow = (headerPos, maxDistance, sheetData, searchPattern) => {
  for (let i = 1; i <= maxDistance; i++) {
    const rowIndex = headerPos.row + i;
    if (rowIndex >= sheetData.length) break;

    const row = sheetData[rowIndex];
    if (headerPos.col >= row.length) continue;

    const cellValue = row[headerPos.col];
    if (cellValue && cellValue !== "") {
      if (!searchPattern || searchPattern.test(cellValue.toString())) {
        return cellValue;
      }
    }
  }

  return null;
};

/**
 * Search for value to the left of header
 */
const searchLeft = (headerPos, maxDistance, sheetData, searchPattern) => {
  const row = sheetData[headerPos.row];

  for (let i = 1; i <= maxDistance; i++) {
    const colIndex = headerPos.col - i;
    if (colIndex < 0) break;

    const cellValue = row[colIndex];
    if (cellValue && cellValue !== "") {
      if (!searchPattern || searchPattern.test(cellValue.toString())) {
        return cellValue;
      }
    }
  }

  return null;
};

/**
 * Search for value above header
 */
const searchAbove = (headerPos, maxDistance, sheetData, searchPattern) => {
  for (let i = 1; i <= maxDistance; i++) {
    const rowIndex = headerPos.row - i;
    if (rowIndex < 0) break;

    const row = sheetData[rowIndex];
    if (headerPos.col >= row.length) continue;

    const cellValue = row[headerPos.col];
    if (cellValue && cellValue !== "") {
      if (!searchPattern || searchPattern.test(cellValue.toString())) {
        return cellValue;
      }
    }
  }

  return null;
};

/**
 * Helper function to validate dates
 */
const isValidDate = (value) => {
  if (!value) return false;
  const date = new Date(value);
  return !isNaN(date.getTime());
};

// Export the enhanced preset configs
export const ENHANCED_PRESET_CONFIGS = {
  TRUCKING_LOAD: {
    ...PRESET_CONFIGS.TRUCKING_LOAD,
    multiSheet: MULTI_SHEET_CONFIGS.TRUCKING_LOAD,
    useIntelligentSelection: true,
  },

  SERVICE_JOBS: {
    ...PRESET_CONFIGS.SERVICE_JOBS,
    multiSheet: MULTI_SHEET_CONFIGS.SERVICE_JOBS,
    useIntelligentSelection: true,
  },

  // Fallback to original configs
  ...PRESET_CONFIGS,
};

/**
 * Example configurations for different Excel layout patterns
 */
export const DIRECTIONAL_SEARCH_EXAMPLES = {
  // Form-style layout where labels are on the left, values on the right
  FORM_STYLE_RIGHT: {
    "Load Number:": {
      target: "loadNumber",
      searchDirection: "right",
      maxDistance: 3,
    },
    "Date:": {
      target: "transactionDate",
      searchDirection: "right",
      maxDistance: 2,
    },
    "Rate:": {
      target: "baseRate",
      searchDirection: "right",
      maxDistance: 2,
    },
  },

  // Vertical form layout where labels are above values
  FORM_STYLE_BELOW: {
    "Customer Information": {
      target: "customerName",
      searchDirection: "below",
      maxDistance: 3,
      searchPattern: /^[A-Za-z\s&.,'-]+$/, // Pattern to match customer names
    },
    "Load Details": {
      target: "loadNumber",
      searchDirection: "below",
      maxDistance: 2,
      searchPattern: /^[A-Z0-9\-]+$/, // Pattern to match load numbers
    },
    "Delivery Address": {
      target: "deliveryAddress1",
      searchDirection: "below",
      maxDistance: 5,
    },
  },

  // Mixed layout with some fields right, some below
  MIXED_LAYOUT: {
    "Load #": {
      target: "loadNumber",
      searchDirection: "right",
      maxDistance: 2,
    },
    "Customer Info": {
      target: "customerName",
      searchDirection: "below",
      maxDistance: 3,
    },
    Rate: {
      target: "baseRate",
      searchDirection: "right",
      maxDistance: 2,
    },
    Notes: {
      target: "note",
      searchDirection: "below",
      maxDistance: 5,
    },
  },
};

/**
 * Helper function to parse numeric values that may contain commas
 * @param {string|number} value - The value to parse
 * @returns {number} - The parsed numeric value
 */
const parseNumericValue = (value) => {
  if (typeof value === "number") return value;
  if (!value || value === "") return 0;

  // Convert to string, remove commas, then parse
  const cleanValue = String(value).replace(/,/g, "");
  const numValue = parseFloat(cleanValue);
  return isNaN(numValue) ? 0 : numValue;
};

/**
 * Create comprehensive legs from order data grouped by drop number
 * Captures ALL data from ORDERS sheet and structures it for backend processing
 */
const createLegsFromData = (dataBySheet, config) => {
  const legConfig = config.legCreation;
  const sourceSheetData = dataBySheet[legConfig.sourceSheet] || [];

  if (sourceSheetData.length === 0) {
    return [];
  }

  // First pass: Group orders with valid drop numbers
  const ordersByDrop = {};
  const ordersWithoutDrop = [];

  // Helper function to find grouping field with case-insensitive, space-tolerant matching
  const findGroupingField = (order, targetField) => {
    // Try exact match first
    if (order[targetField] !== undefined) {
      return order[targetField];
    }

    // Normalize target field (lowercase, remove spaces)
    const normalizedTarget = targetField.toLowerCase().replace(/\s+/g, "");

    // Try to find matching key in order
    for (const key in order) {
      if (order.hasOwnProperty(key)) {
        const normalizedKey = key.toLowerCase().replace(/\s+/g, "");
        if (normalizedKey === normalizedTarget) {
          return order[key];
        }
      }
    }

    return undefined;
  };

  sourceSheetData.forEach((order) => {
    const dropNumber = findGroupingField(order, legConfig.groupingField);

    if (
      dropNumber &&
      dropNumber !== "" &&
      dropNumber !== "0" &&
      dropNumber !== "unknown"
    ) {
      if (!ordersByDrop[dropNumber]) {
        ordersByDrop[dropNumber] = [];
      }
      ordersByDrop[dropNumber].push(order);
    } else {
      ordersWithoutDrop.push(order);
    }
  });

  // Second pass: Try to assign orders without drop numbers to existing drops
  ordersWithoutDrop.forEach((order) => {
    const customerName = (
      order[legConfig.legFields.customer.name] || ""
    ).trim();
    const city = (order[legConfig.legFields.deliveryAddress.city] || "").trim();
    const address1 = (
      order[legConfig.legFields.deliveryAddress.line1] || ""
    ).trim();
    // Check multiple possible field names for order number
    const orderNumberField = legConfig.legFields.core.orderNumberAlternatives
      ? legConfig.legFields.core.orderNumberAlternatives.find(
          (field) => order[field]
        )
      : legConfig.legFields.core.orderNumber;
    const orderNumber = (
      order[orderNumberField] ||
      order[legConfig.legFields.core.orderNumber] ||
      ""
    ).trim();

    // Find matching drop by customer and location
    let matchedDrop = null;
    let matchReason = "";

    for (const [dropNumber, existingOrders] of Object.entries(ordersByDrop)) {
      const existingOrder = existingOrders[0]; // Use first order as reference
      const existingCustomer = (
        existingOrder[legConfig.legFields.customer.name] || ""
      ).trim();
      const existingCity = (
        existingOrder[legConfig.legFields.deliveryAddress.city] || ""
      ).trim();
      const existingAddress1 = (
        existingOrder[legConfig.legFields.deliveryAddress.line1] || ""
      ).trim();

      // Match by customer name and city (both must be non-empty)
      if (
        customerName &&
        existingCustomer &&
        city &&
        existingCity &&
        customerName === existingCustomer &&
        city === existingCity
      ) {
        matchedDrop = dropNumber;
        matchReason = "customer/city match";
        break;
      }

      // Fallback: match by address line 1 (must be non-empty and substantial)
      if (
        address1 &&
        existingAddress1 &&
        address1.length > 10 &&
        address1 === existingAddress1
      ) {
        matchedDrop = dropNumber;
        matchReason = "address match";
        break;
      }

      // Additional fallback: match by city only if customer names are both empty
      if (
        !customerName &&
        !existingCustomer &&
        city &&
        existingCity &&
        city === existingCity
      ) {
        matchedDrop = dropNumber;
        matchReason = "city-only match (no customer names)";
        break;
      }
    }

    if (matchedDrop) {
      ordersByDrop[matchedDrop].push(order);
    } else {
      // Create a new drop for unmatched orders if they have sufficient data
      if (city || address1) {
        const newDropNumber = `unassigned_${
          Object.keys(ordersByDrop).length + 1
        }`;
        ordersByDrop[newDropNumber] = [order];
      }
    }
  });

  // Create comprehensive leg objects
  const legs = Object.entries(ordersByDrop).map(([dropNumber, orders]) => {
    // Use first order for leg-level information
    const primaryOrder = orders[0];

    // Build comprehensive delivery address for backend lookup
    const addressComponents = legConfig.legFields.deliveryAddress.fullAddress
      .map((field) => primaryOrder[field] || "")
      .filter((component) => component.trim() !== "");

    const fullAddress = addressComponents.join(", ").trim();

    // Calculate aggregated totals
    const totals = {
      totalOrderQuantity: orders.reduce(
        (sum, order) =>
          sum +
          (parseNumericValue(
            order[legConfig.legFields.product.orderQuantity]
          ) || 0),
        0
      ),
      totalOrderWeight: orders.reduce(
        (sum, order) =>
          sum +
          (parseNumericValue(order[legConfig.legFields.product.orderWeight]) ||
            0),
        0
      ),
      totalBackOrderQuantity: orders.reduce(
        (sum, order) =>
          sum +
          (parseNumericValue(
            order[legConfig.legFields.product.backOrderQuantity]
          ) || 0),
        0
      ),
      totalBackOrderWeight: orders.reduce(
        (sum, order) =>
          sum +
          (parseNumericValue(
            order[legConfig.legFields.product.backOrderWeight]
          ) || 0),
        0
      ),
    };

    // Get unique values for concatenated fields
    const uniqueShipTickets = [
      ...new Set(
        orders
          .map((order) => order[legConfig.legFields.shipping.shipTicket])
          .filter((ticket) => ticket && ticket.trim() !== "")
      ),
    ];
    const uniquePOs = [
      ...new Set(
        orders
          .map((order) => order[legConfig.legFields.customer.purchaseOrder])
          .filter((po) => po && po.trim() !== "")
      ),
    ];

    // Create comprehensive leg object
    const leg = {
      // Core identification
      dropNumber: dropNumber,
      orderCount: orders.length,
      orderNumber: (() => {
        // Check multiple possible field names for order number
        const orderNumberField = legConfig.legFields.core
          .orderNumberAlternatives
          ? legConfig.legFields.core.orderNumberAlternatives.find(
              (field) => primaryOrder[field]
            )
          : legConfig.legFields.core.orderNumber;
        return (
          primaryOrder[orderNumberField] ||
          primaryOrder[legConfig.legFields.core.orderNumber] ||
          ""
        ).trim();
      })(),

      // Customer information
      customer: {
        name: (primaryOrder[legConfig.legFields.customer.name] || "").trim(),
        purchaseOrders: uniquePOs, // All unique POs for this leg
        primaryPO: uniquePOs[0] || "", // Primary PO for quick reference
      },

      // Complete delivery address (structured for backend lookup)
      deliveryAddress: {
        line1: (
          primaryOrder[legConfig.legFields.deliveryAddress.line1] || ""
        ).trim(),
        line2: (
          primaryOrder[legConfig.legFields.deliveryAddress.line2] || ""
        ).trim(),
        line3: (
          primaryOrder[legConfig.legFields.deliveryAddress.line3] || ""
        ).trim(),
        line4: (
          primaryOrder[legConfig.legFields.deliveryAddress.line4] || ""
        ).trim(),
        city: (
          primaryOrder[legConfig.legFields.deliveryAddress.city] || ""
        ).trim(),
        state: (
          primaryOrder[legConfig.legFields.deliveryAddress.state] || ""
        ).trim(),
        zip: (
          primaryOrder[legConfig.legFields.deliveryAddress.zip] || ""
        ).trim(),
        fullAddress: fullAddress, // Combined address for backend location lookup
        addressComponents: addressComponents, // Array of non-empty components
      },

      // Shipping and logistics (aggregated)
      shipping: {
        shipTickets: uniqueShipTickets, // All unique ship tickets for this leg
        primaryShipTicket: uniqueShipTickets[0] || "", // Primary ship ticket
        freightHandlingCode: (
          primaryOrder[legConfig.legFields.shipping.freightHandlingCode] || ""
        ).trim(),
        totalLoadPercent: orders.reduce(
          (sum, order) =>
            sum +
            (parseNumericValue(
              order[legConfig.legFields.shipping.loadPercent]
            ) || 0),
          0
        ),
        totalPacksOrPallets: orders.reduce(
          (sum, order) =>
            sum +
            (parseNumericValue(
              order[legConfig.legFields.shipping.packsOrPallets]
            ) || 0),
          0
        ),
      },

      // Status information (from primary order)
      status: {
        holdCode: (
          primaryOrder[legConfig.legFields.status.holdCode] || ""
        ).trim(),
        nextStatus: (
          primaryOrder[legConfig.legFields.status.nextStatus] || ""
        ).trim(),
      },

      // Dates (from primary order)
      dates: {
        orderDate: (
          primaryOrder[legConfig.legFields.dates.orderDate] || ""
        ).trim(),
        requestDate: (
          primaryOrder[legConfig.legFields.dates.requestDate] || ""
        ).trim(),
      },

      // Aggregated totals for the entire leg
      totals: totals,

      // Complete items array - ALL line items for this leg
      items: orders.map((order, index) => ({
        // Core identification
        itemIndex: index + 1,
        orderNumber: (() => {
          // Check multiple possible field names for order number
          const orderNumberField = legConfig.legFields.core
            .orderNumberAlternatives
            ? legConfig.legFields.core.orderNumberAlternatives.find(
                (field) => order[field]
              )
            : legConfig.legFields.core.orderNumber;
          return (
            order[orderNumberField] ||
            order[legConfig.legFields.core.orderNumber] ||
            ""
          ).trim();
        })(),
        lineNumber: (order[legConfig.legFields.core.lineNumber] || "").trim(),

        // Product details
        product: {
          partNumber: (
            order[legConfig.legFields.product.partNumber] || ""
          ).trim(),
          description: (
            order[legConfig.legFields.product.description] || ""
          ).trim(),
          orderQuantity:
            parseNumericValue(
              order[legConfig.legFields.product.orderQuantity]
            ) || 0,
          orderWeight:
            parseNumericValue(order[legConfig.legFields.product.orderWeight]) ||
            0,
          backOrderQuantity:
            parseNumericValue(
              order[legConfig.legFields.product.backOrderQuantity]
            ) || 0,
          backOrderWeight:
            parseNumericValue(
              order[legConfig.legFields.product.backOrderWeight]
            ) || 0,
        },

        // Item-specific shipping info
        shipping: {
          shipTicket: (
            order[legConfig.legFields.shipping.shipTicket] || ""
          ).trim(),
          loadPercent:
            parseNumericValue(
              order[legConfig.legFields.shipping.loadPercent]
            ) || 0,
          packsOrPallets:
            parseNumericValue(
              order[legConfig.legFields.shipping.packsOrPallets]
            ) || 0,
        },

        // Item-specific customer info
        customer: {
          purchaseOrder: (
            order[legConfig.legFields.customer.purchaseOrder] || ""
          ).trim(),
        },

        // Raw order data (for debugging/future use)
        rawOrderData: order,
      })),

      // Summary for quick reference
      summary: {
        itemCount: orders.length,
        uniquePartNumbers: [
          ...new Set(
            orders
              .map((order) => order[legConfig.legFields.product.partNumber])
              .filter((part) => part && part.trim() !== "")
          ),
        ].length,
        hasBackOrders: totals.totalBackOrderQuantity > 0,
        multipleShipTickets: uniqueShipTickets.length > 1,
        multiplePOs: uniquePOs.length > 1,
      },
    };

    return leg;
  });

  // Sort legs by drop number
  legs.sort((a, b) => {
    const aNum = parseInt(a.dropNumber) || 0;
    const bNum = parseInt(b.dropNumber) || 0;
    return aNum - bNum;
  });

  return legs;
};

/**
 * Resolve locations for legs during extraction
 * This function calls the backend API to pre-resolve location IDs
 */
export const resolveLocationsForLegs = async (
  legs,
  parentCompany,
  clientId
) => {
  try {
    // Transform legs to only include essential data for location matching
    const simplifiedLegs = legs.map((leg, index) => ({
      // Reference data
      dropNumber: leg.dropNumber,
      orderNumber: leg.orderNumber,

      // Essential data for location matching
      customerName: leg.customer?.name,
      deliveryAddress1: leg.deliveryAddress?.line1,
      deliveryAddress2: leg.deliveryAddress?.line2,
      deliveryAddress3: leg.deliveryAddress?.line3,
      deliveryAddress4: leg.deliveryAddress?.line4,
      deliveryCity: leg.deliveryAddress?.city,
      deliveryState: leg.deliveryAddress?.state,
      deliveryZip: leg.deliveryAddress?.zip,

      // Keep track of original index for mapping results back
      originalIndex: index,
    }));

    // Use the existing request utility to get proper headers and authentication
    const { default: request } = await import("../../utils/request");

    const response = await request("/locations/resolve-extraction", {
      method: "POST",
      data: {
        parentCompany,
        clientId,
        legs: simplifiedLegs, // Send simplified data instead of full legs
      },
    });

    const result = response.data;

    if (result.status === "success") {
      // Helper function to normalize location object (convert id to _id)
      const normalizeLocation = (location) => {
        if (!location) return null;
        return {
          ...location,
          _id: location.id || location._id, // Ensure _id is set from id
          // Keep the address structure if it exists
          address: location.address || null,
        };
      };

      // Map the resolved locations back to the original full leg objects
      const legsWithLocations = legs.map((originalLeg, index) => {
        const resolvedLeg = result.data.legs.find(
          (leg) => leg.originalIndex === index
        );
        // Compute mileage from the best available source.
        // defaultMileage is the user-set billing standard and takes priority.
        // mileage is the computed distance from the provider.
        // totalMiles is a derived placeholder that can be 0 on cold-start.
        const extractedMiles =
          resolvedLeg?.route?.defaultMileage > 0
            ? resolvedLeg.route.defaultMileage
            : resolvedLeg?.route?.mileage > 0
              ? resolvedLeg.route.mileage
              : resolvedLeg?.route?.distance > 0
                ? resolvedLeg.route.distance
                : resolvedLeg?.totalMiles > 0
                  ? resolvedLeg.totalMiles
                  : originalLeg?.totalMiles || null;

        return {
          ...originalLeg,
          resolvedLocation: normalizeLocation(resolvedLeg?.resolvedLocation),
          origin: normalizeLocation(resolvedLeg?.resolvedOriginLocation),
          destination: normalizeLocation(resolvedLeg?.resolvedLocation),
          route: resolvedLeg?.route || null,
          // Preserve totalMiles from extraction, fallback to route.distance if available
          totalMiles: extractedMiles,
          // Map mileage fields the form expects for the LegMileageSelector dropdown
          defaultMileage:
            resolvedLeg?.route?.defaultMileage || extractedMiles || 0,
          // Only set billedMiles when there's explicit data from the route.
          // Defaulting to extractedMiles would make the UI think custom billing
          // was applied when it wasn't.
          billedMiles:
            resolvedLeg?.route?.billedMiles != null
              ? resolvedLeg.route.billedMiles
              : null,
        };
      });

      return legsWithLocations;
    } else {
      console.error("❌ Location resolution failed:", result.message);
      return legs; // Return original legs if resolution fails
    }
  } catch (error) {
    console.error("💥 Error resolving locations:", error);
    return legs; // Return original legs if API call fails
  }
};

/**
 * Enhanced intelligent data selection with location pre-resolution
 * Now merges data from all relevant sheets and resolves locations
 */
export const selectBestDataForFormWithLocationResolution = async (
  extractedData,
  configType = "TRUCKING_LOAD",
  parentCompany,
  clientId
) => {
  // First, get the basic extraction result
  const basicResult = selectBestDataForForm(extractedData, configType);

  // If we have legs and the required parameters, resolve locations
  if (
    basicResult.legs &&
    basicResult.legs.length > 0 &&
    parentCompany &&
    clientId
  ) {
    try {
      const legsWithLocations = await resolveLocationsForLegs(
        basicResult.legs,
        parentCompany,
        clientId
      );

      return {
        ...basicResult,
        legs: legsWithLocations,
        metadata: {
          ...basicResult.metadata,
          locationsPreResolved: true,
          locationsResolvedCount: legsWithLocations.filter(
            (leg) => leg.resolvedLocation
          ).length,
        },
      };
    } catch (error) {
      console.error(
        "⚠️ Location pre-resolution failed, continuing without:",
        error
      );
      return {
        ...basicResult,
        metadata: {
          ...basicResult.metadata,
          locationsPreResolved: false,
          locationResolutionError: error.message,
        },
      };
    }
  }

  return basicResult;
};
